import React, { useState } from 'react';
import { motion } from 'framer-motion';
import EvidenceBg from '../images/ui/dark-blue-bg.jpg';
import Chart1 from '../images/evidence/evidence-chart-1.svg';
import Chart2 from '../images/evidence/evidence-chart-2.svg';
import Modal from './Modal';
import BrainResponse from './Evidence/BrainResponse';
import SpeechUnderstanding from './Evidence/SpeechUnderstanding';
import MagentaLine from './elements/MagentaLine';
import VideoBg from '../video/evidence-bg.mp4';

const BASE_TIME = 3.2;
const TEST_SETUP_BUTTON_STYLES = {
  width: '270px',
  height: '42px',
  backgroundColor: 'transparent',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  fontWeight: 'bold',
  color: 'rgba(255, 255, 255, 1)',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  transition: 'all 0.3s',
  cursor: 'pointer',
  marginTop: '50px',
  outline: 'none',
};

export default function Evidence() {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState(0);
  const item = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: 0.8, delay: BASE_TIME + 1.8 } },
  };
  function openModal(val) {
    setIsOpen(true);
    setModalContent(val);
  }

  return (
    <div id="evidence-wrapper">
      <img src={EvidenceBg} className="full-bg" />
      <motion.video
        src={VideoBg}
        className="full-bg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.5 }}
        loop
        autoPlay
        muted
      />
      <header>
        <MagentaLine height="105px" left="40px" />
        <div className="wordwrap" style={{ left: '22px' }}>
          <motion.div
            className="first-line"
            style={{
              fontSize: '24px',
              top: '42px',
              left: '45px',
            }}
            initial={{ x: -982 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Helping patients jump into life's{' '}
            <strong>amazing complexity</strong>
          </motion.div>
          <motion.div
            className="second-line"
            style={{
              fontSize: '17px',
              top: '85px',
              left: '45px',
              letterSpacing: '0.4px',
            }}
            initial={{ x: -982 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            With access to a full, precisely balanced sound scene
          </motion.div>
        </div>
      </header>

      <motion.button
        id="test-button-1"
        className="test-button"
        onClick={() => openModal(1)}
        type="button"
        style={TEST_SETUP_BUTTON_STYLES}
        variants={item}
        initial="hidden"
        animate="show"
      >
        Test Setup
      </motion.button>
      <motion.button
        id="test-button-2"
        className="test-button"
        onClick={() => openModal(2)}
        type="button"
        style={TEST_SETUP_BUTTON_STYLES}
        variants={item}
        initial="hidden"
        animate="show"
      >
        Test Setup
      </motion.button>
      <motion.div
        id="ball-copy"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: BASE_TIME + 0.6 }}
      >
        MoreSound
        <br /> Intelligence™
        <br /> is proven to make <br />
        the full sound scene
        <br /> <strong>60% clearer**</strong>
      </motion.div>
      <div
        id="chart-1"
        className="chart"
        variants={item}
        initial="hidden"
        animate="show"
      >
        <motion.div
          className="heading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: BASE_TIME }}
        >
          Brain Response
        </motion.div>
        <motion.div
          className="gradient"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: BASE_TIME }}
        />
        <div className="bar-wrapper">
          <motion.div
            className="bar"
            id="bar-1"
            initial={{ height: 0 }}
            animate={{ height: '149px' }}
            transition={{ duration: 0.4, delay: BASE_TIME + 0.6 }}
          />
          <motion.div
            className="bar"
            id="bar-2"
            initial={{ height: 0 }}
            animate={{ height: '183px' }}
            transition={{ duration: 0.4, delay: BASE_TIME + 1 }}
          />
          <motion.div
            className="val"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: BASE_TIME + 1.6 }}
          >
            +30%
          </motion.div>
        </div>
        <motion.img
          src={Chart1}
          alt=""
          className="chart"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: BASE_TIME }}
        />
      </div>
      <div
        id="chart-2"
        className="chart"
        variants={item}
        initial="hidden"
        animate="show"
      >
        <motion.div
          className="heading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: BASE_TIME }}
        >
          Speech Understanding
        </motion.div>
        <motion.div
          className="gradient"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: BASE_TIME }}
        />
        <div className="bar-wrapper">
          <motion.div
            className="bar"
            id="bar-3"
            initial={{ height: 0 }}
            animate={{ height: '112px' }}
            transition={{ duration: 0.4, delay: BASE_TIME + 0.6 }}
          />
          <motion.div
            className="bar"
            id="bar-4"
            initial={{ height: 0 }}
            animate={{ height: '206px' }}
            transition={{ duration: 0.4, delay: BASE_TIME + 1 }}
          />
          <motion.div
            className="val"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: BASE_TIME + 1.6 }}
          >
            +15%
          </motion.div>
        </div>
        <motion.img
          src={Chart2}
          alt=""
          className="chart"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: BASE_TIME }}
        />
      </div>
      <motion.div
        id="caption-1"
        className="caption"
        variants={item}
        initial="hidden"
        animate="show"
      >
        Oticon More™ delivers{' '}
        <strong>
          30% more
          <br />
          sound
        </strong>{' '}
        to the brain to help patients
        <br />
        navigate the environment*
      </motion.div>
      <motion.div
        id="caption-2"
        className="caption"
        variants={item}
        initial="hidden"
        animate="show"
      >
        Increases speech understanding by yet
        <br />
        <strong>another 15%*</strong>
      </motion.div>
      <motion.div
        className="footnote"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: BASE_TIME + 3 }}
      >
        * Compared to Oticon Opn S™, Santurette, et al., 2020. Oticon More
        clinical evidence. Oticon Whitepaper
        <br />
        ** EEG testing with MoreSound Intelligence in on vs. off setting, from
        Santurette et al
      </motion.div>
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        {modalContent === 1 ? <BrainResponse /> : ''}
        {modalContent === 2 ? <SpeechUnderstanding /> : ''}
      </Modal>
    </div>
  );
}
